import axios from 'axios';

export default class AiChatWidget {
  /**
   * Initializes the chat widget with the provided configuration.
   * @param {Object} config - The configuration object.
   * @param {string} config.promptInputId - ID of the input element for typing prompts.
   * @param {string} config.sendButtonId - ID of the button to trigger sending the prompt.
   * @param {string} config.chatContainerId - ID of the container element where chat history is shown.
   * @param {string} config.accessKey - The access key for AI backend integration.
   * @param {string} config.apiUrl - The API URL for the backend integration.
   */
  constructor({ promptInputId, sendButtonId, chatContainerId, token, userId, projectId, language, apiUrl }) {
    this.promptInputId = promptInputId;
    this.sendButtonId = sendButtonId;
    this.chatContainerId = chatContainerId;
    this.token = token;
    this.userId = userId;
    this.projectId = projectId;
    this.language = language;
    this.apiUrl = apiUrl || 'YOUR_BACKEND_API_URL_HERE';

    this.promptInput = null;
    this.sendButton = null;
    this.chatContainer = null;
    this.sessionId = null;
    this.handleSendClick = this.handleSendMessage.bind(this);
    this.handleKeyPress = this.handleInputKeyPress.bind(this);
  }

  /**
   * Hooks into the existing DOM elements and sets up event listeners.
   * Call this method after the DOM is fully loaded or inside a React useEffect hook.
   */
  async init() {
    // Dynamically inject marked.js parser snippet as requested
    if (!document.getElementById('marked-js-cdn')) {
      const script = document.createElement('script');
      script.id = 'marked-js-cdn';
      script.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
      document.head.appendChild(script);
    }

    // 1. Hook into existing DOM elements
    this.promptInput = document.getElementById(this.promptInputId);
    this.sendButton = document.getElementById(this.sendButtonId);
    this.chatContainer = document.getElementById(this.chatContainerId);

    // Ensure all required elements are found before proceeding
    if (!this.promptInput || !this.sendButton || !this.chatContainer) {
      console.error('AiChatWidget Error: One or more required DOM elements were not found.', {
        promptInputFound: !!this.promptInput,
        sendButtonFound: !!this.sendButton,
        chatContainerFound: !!this.chatContainer
      });
      return;
    }

    // Remove existing event listeners if init is called multiple times
    this.sendButton.removeEventListener('click', this.handleSendClick);
    this.promptInput.removeEventListener('keypress', this.handleKeyPress);

    // 2. Attach click event to the Send button
    this.sendButton.addEventListener('click', this.handleSendClick);

    // 3. Optional: Allow 'Enter' key to also send the message
    this.promptInput.addEventListener('keypress', this.handleKeyPress);

    // 4. Fetch session ID on initialization
    await this.fetchSessionId();

    console.log('AiChatWidget initialized successfully.');
  }

  /**
   * Fetches a session ID from the backend using the provided user and project data.
   */
  async fetchSessionId() {
    if (!this.userId || !this.projectId || !this.token) {
      console.warn('AiChatWidget: Missing userId, projectId, or token. Cannot fetch session ID.');
      return;
    }

    try {
      const sessionUrl = this.apiUrl + '/chat/create-new';

      const response = await axios.post(sessionUrl, {
        user_id: this.userId,
        project_key: this.projectId,
        lang: this.language
      }, {
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${this.token}`
        },
        timeout: 60000, // 60 seconds timeout to prevent premature aborts,
        withCredentials: false
      });

      const data = response.data;
      this.sessionId = data.sess_id || data.sessionId || data.session_id || data.session || null;
      console.log('AiChatWidget session initialized successfully:', this.sessionId);

      // If there is an introductory message from the backend, display it immediately
      if (data.msg) {
        this.appendMessageToChat('AI', data.msg);
      }
    } catch (error) {
      console.error('AiChatWidget Error: Failed to fetch session ID.', error);
    }
  }

  handleInputKeyPress(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent form submission if input is inside a form
      this.handleSendMessage();
    }
  }

  /**
   * Handles the process of sending a user message, displaying it, and fetching the AI response.
   */
  async handleSendMessage() {
    const userMessage = this.promptInput.value.trim();

    // Prevent sending empty prompts
    if (!userMessage) return;

    // Display the user's message in the chat container
    this.appendMessageToChat('User', userMessage);

    // Clear the input field immediately to improve perceived responsiveness
    this.promptInput.value = '';

    // Auto resize text area back to normal if needed
    if (this.promptInput.style.height) {
      this.promptInput.style.height = 'auto';
    }

    // Show a loading indicator
    const loadingId = this.appendLoadingIndicator();

    try {
      // Send the input value to the backend API
      const aiResponse = await this.apiCall(userMessage);

      // Remove loading indicator and display the AI's actual response
      this.removeElementById(loadingId);
      this.appendMessageToChat('AI', aiResponse);
    } catch (error) {
      console.error('AiChatWidget Error: Failed to fetch AI response.', error);
      this.removeElementById(loadingId);
      this.appendMessageToChat('System', 'Failed to communicate with the server. Please check your connection.');
    }
  }

  /**
   * Appends a new message block to the chat container.
   * @param {string} sender - Who sent the message (e.g., 'User', 'AI').
   * @param {string} text - The message text.
   */
  appendMessageToChat(sender, text) {
    if (!this.chatContainer) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'ask-ai-message-wrapper';

    const row = document.createElement('div');
    row.className = `ask-ai-row ${sender === 'User' ? 'ask-ai-row--user' : 'ask-ai-row--bot'}`;

    const bubble = document.createElement('div');
    bubble.className = `ask-ai-bubble ${sender === 'User' ? 'ask-ai-bubble--user' : 'ask-ai-bubble--bot'}`;

    // Parse markdown for AI responses if marked is available, otherwise raw text
    if (sender === 'AI') {
      if (window.marked && window.marked.parse) {
        // Parse markdown text payload cleanly into innerHTML
        bubble.innerHTML = window.marked.parse(text);
      } else {
        // Fallback
        bubble.innerText = text;
      }
    } else {
      bubble.innerText = text;
    }

    row.appendChild(bubble);
    wrapper.appendChild(row);

    // For AI messages, optionally add the volume icon structure below to match the UI
    // if (sender === 'AI') {
    //   const volumeWrapper = document.createElement('div');
    //   volumeWrapper.className = 'ask-ai-volume-wrapper';
    //   volumeWrapper.innerHTML = `
    //     <div class="cursor-pointer" style="display:inline-flex; align-items:center;">
    //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
    //     </div>
    //   `;
    //   // Adding a simple click to speak functionality to match original behavior
    //   volumeWrapper.querySelector('.cursor-pointer').addEventListener('click', () => {
    //     if (!window.speechSynthesis) return;
    //     if (window.speechSynthesis.speaking) {
    //       window.speechSynthesis.cancel();
    //     } else {
    //       const msg = new SpeechSynthesisUtterance(text);
    //       msg.lang = "en-US";
    //       window.speechSynthesis.speak(msg);
    //     }
    //   });
    //   wrapper.appendChild(volumeWrapper);
    // }

    this.chatContainer.appendChild(wrapper);

    this.scrollToBottom();
  }


  /**
   * Smoothly scrolls the chat container to the bottom.
   */
  scrollToBottom() {
    if (!this.chatContainer) return;

    const scroll = () => {
      this.chatContainer.scrollTo({
        top: this.chatContainer.scrollHeight,
        behavior: 'smooth'
      });

      // Fallback: Ensure the last message is visible
      const lastMsg = this.chatContainer.lastElementChild;
      if (lastMsg) {
        lastMsg.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Execute multiple times to catch delayed layout changes (e.g. markdown rendering)
    scroll();
    setTimeout(scroll, 50);
    setTimeout(scroll, 250);
  }

  /**
   * Appends a temporary "AI is typing..." indicator.
   * @returns {string} The unique ID of the loading element so it can be removed later.
   */
  appendLoadingIndicator() {
    const loadingId = `loading-${Date.now()}`;

    const wrapper = document.createElement('div');
    wrapper.id = loadingId;
    wrapper.className = 'ask-ai-message-wrapper';

    const row = document.createElement('div');
    row.className = 'ask-ai-row ask-ai-row--bot';
    row.style.marginBottom = '16px';

    const bubble = document.createElement('div');
    bubble.className = 'ask-ai-bubble ask-ai-bubble--bot';

    const typing = document.createElement('div');
    typing.className = 'ask-ai-typing';
    typing.innerHTML = '<span></span><span></span><span></span>';

    bubble.appendChild(typing);
    row.appendChild(bubble);
    wrapper.appendChild(row);

    this.chatContainer.appendChild(wrapper);
    // this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
    this.scrollToBottom();
    return loadingId;
  }

  /**
   * Utility to remove a specific element by ID.
   * @param {string} elementId - The ID of the element to remove.
   */
  removeElementById(elementId) {
    const el = document.getElementById(elementId);
    if (el) {
      el.remove();
    }
  }

  /**
   * The actual fetch API call to the backend.
   * @param {string} message - The message typed by the user.
   * @returns {Promise<string>} The AI text response.
   */
  async apiCall(message) {
    if (!this.token) { // Using this.token
      console.warn('No token provided for API call. Returning mock response.');
      return new Promise((resolve) => setTimeout(() => resolve(`Mock response: ${message}`), 1000));
    }

    try {
      const response = await axios.post(this.apiUrl + '/chat/message', {
        msg: message,
        user_id: this.userId,
        project_key: this.projectId,
        sess_id: this.sessionId,
        lang: this.language
      }, {
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${this.token}`
        },
        timeout: 60000, // 60s timeout here as well
        withCredentials: false
      });

      const data = response.data;
      // Assuming your API returns JSON like: { answer: "Hello there" }
      return data.answer || data.msg || "No valid response format received.";
    } catch (error) {
      // For development, fallback to mock if URL is placeholder
      if (this.apiUrl === 'YOUR_BACKEND_API_URL_HERE' || error.message.includes('Failed to fetch') || error.message.includes('timeout')) {
        return new Promise((resolve) => setTimeout(() => resolve(`[Mock API] This is a simulated response to: "${message}"`), 1200));
      }
      throw error;
    }
  }
}

